import {
  AgGridReact,
  type CustomCellEditorProps,
} from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, type FC } from 'react';

import {
  CellStyleModule,
  ColDef,
  CustomEditorModule,
  IServerSideGetRowsParams,
  ModuleRegistry,
  PinnedRowModule,
  RenderApiModule,
  RowApiModule,
  TextEditorModule,
  ValidationModule,
  type GetContextMenuItemsParams,
  type GetRowIdParams,
  type GridReadyEvent,
} from '@mint-lib/ag-grid/community';
import { LeavePageModal } from '@mint-lib/ag-grid/components';
import { TextEditor } from '@mint-lib/ag-grid/editors';
import {
  ColumnApiModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  LicenseManager,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  type AutoGroupColumnDef,
  type ColumnMovedEvent,
  type ColumnVisibleEvent,
  type SideBarDef,
} from '@mint-lib/ag-grid/enterprise';
import {
  colDefWithErrorHandling,
  createContext,
} from '@mint-lib/ag-grid/factory';
import { useChannels } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { ChevronRight, Delete, Duplicate, Erase } from '@mint-lib/icons';
import { useIsAllowedPermission, useProfile } from '@mint-lib/profile-helpers';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { parseISO } from 'date-fns';

import { useColumnsMutationApi } from '../../../../../api/mutations/useColumnsMutationApi.js';
import { useUpdateColumn } from '../../../../../api/mutations/useUpdateColumn.js';
import { useTotalsRowApi } from '../../../../../api/queries/useTotalsRowApi.js';
import { usePlanningGridApi } from '../../../../../atoms/gridApi.js';
import { TOTALS_ROW_ID } from '../../../../../consts/grid.consts.jsx';
import { MP_LEVELS, NO_DECIMALS } from '../../../../../consts/index.js';
import { useCellEditorHandlers } from '../../../../../hooks/useCellEditorHandlers.js';
import { useChosenVersionId } from '../../../../../hooks/useChosenVersionId.js';
import { useGoalOptionGroups } from '../../../../../hooks/useGoalOptionGroups.js';
import { useSetMediaDataToDelete } from '../../../../../hooks/useSetMediaDataToDelete.js';
import type { CellEditorParamsMapType } from '../../../../../types/grid.types.js';
import type { MpLevelNames } from '../../../../../types/shared.js';
import {
  calculateDateConstraints,
  getColName,
  getIsClearValueDisabled,
  isUserColumnChange,
  prepareColumns,
} from '../../../../../utils/grid.utils.js';
import { AddRowProvider } from './AddRowProvider/AddRowProvider.jsx';
import { AddRowMenuItem } from './contextMenuItems/AddRowMenuItem';
import MenuItemBase from './contextMenuItems/MenuItemBase.jsx';
import { DeleteMediaDataProvider } from './DeleteMediaDataProvider/DeleteMediaDataProvider.jsx';
import DuplicateMediaRow from './DuplicateMediaRow/DuplicateMediaRow.jsx';
import { InfoTooltip } from './InfoTooltip.jsx';
import { NameColumnInnerRenderer } from './NameColumnInnerRenderer/NameColumnInnerRenderer.jsx';
import { TruncatedColumnHeader } from './TruncatedColumnHeader/TruncatedColumnHeader.jsx';

ModuleRegistry.registerModules([
  RenderApiModule,
  PinnedRowModule,
  RowApiModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  ValidationModule,
  TextEditorModule,
  CustomEditorModule,
  CellStyleModule,
  ColumnsToolPanelModule,
  ColumnApiModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

const CACHE_BLOCK_SIZE = 20;
const TOOLTIP_SHOW_DELAY = 0;

type Props = {
  columnsData: NonNullable<MediaPlan.ColumnsResponse['data']>;
  restrictions?: MediaPlan.RestrictionsResponse;
  selectedLevelInfo: {
    id: string;
    level: MpLevelNames;
  };
};

const TableGrid: FC<Props> = ({
  selectedLevelInfo,
  columnsData,
  restrictions,
}) => {
  const [gridApi, setGridApi] = usePlanningGridApi();
  const channelsMap = useChannels();

  const { getCompanyField } = useProfile();
  const companyId = getCompanyField('id');

  const goalGroups = useGoalOptionGroups();

  const { mutateAsync: updateColumn, isPending: isUpdateColumnPending } =
    useUpdateColumn();

  const cellHandlers = useCellEditorHandlers({
    updateColumn,
  });

  const { mutate: updateColumns } = useColumnsMutationApi();

  const { id: chosenId, level: chosenLevel } = selectedLevelInfo;

  const { t } = useTranslation('@myn/planning_v2');

  const isReadOnly = !useIsAllowedPermission('campaignPlanningFeature:modify');

  const flightVersionId = useChosenVersionId();
  const isArchivedVersion = !!flightVersionId;

  const findColumn = useCallback(
    (field: string) => {
      const colName = getColName(field);
      const column = columnsData.items.find((item) => item.id === colName);

      return column;
    },
    [columnsData.items],
  );

  const cellEditorParamsMap = useMemo<Partial<CellEditorParamsMapType>>(
    () => ({
      currency: (params: CustomCellEditorProps<MediaPlan.MediaData>) => {
        const column = findColumn(params.colDef?.field ?? '');

        return {
          currency: params.data.currencyCode,
          maxDecimals: column?.decimalPlaces ?? NO_DECIMALS,
          InputProps: {
            restFormatProps: {
              min: 0,
            },
          },
        };
      },
      select: (params) => {
        const column = findColumn(params.colDef?.field ?? '');

        return {
          type: 'single',
          options: column?.options ?? [],
          disabledOptions: column?.disabledOptions ?? [],
        };
      },
      related_select: (params: CustomCellEditorProps<MediaPlan.MediaData>) => {
        const column = findColumn(params.colDef?.field ?? '');

        return {
          type: 'single',
          mdmContext: 'planning',
          companyId,
          relatedFieldId: getColName(params.colDef?.field ?? ''),
          primaryEntitiesUrnMap: getPrimaryEntitiesUrnMap(
            params.data,
            column?.primaryFieldsUrns ?? [],
          ),
          disabledOptions: column?.disabledOptions ?? [],
        };
      },
      reference: (params: CustomCellEditorProps<MediaPlan.MediaData>) => {
        return {
          groups: goalGroups,
          currency: params.data.currencyCode,
          title: t('Row goal'),
          hint: t('Leave value empty to exclude this row from AI suggestions'),
        };
      },
      percent: (params) => {
        const column = findColumn(params.colDef?.field ?? '');

        return {
          maxDecimals: column?.decimalPlaces ?? NO_DECIMALS,
        };
      },
      decimal: (params) => {
        const column = findColumn(params.colDef?.field ?? '');

        return {
          maxDecimals: column?.decimalPlaces ?? NO_DECIMALS,
        };
      },
      integer: () => {
        return {
          maxDecimals: NO_DECIMALS,
        };
      },

      date: (params) => {
        const isStartDateCol = params?.colDef?.colId?.includes('date_from');
        const isEndDateCol = params?.colDef?.colId?.includes('date_to');

        if (isStartDateCol || isEndDateCol) {
          const { disabledDates } = calculateDateConstraints(
            params,
            restrictions,
          );
          const defaultMonth = deriveDefaultMonth(isStartDateCol, restrictions);

          return {
            disabledDates,
            defaultMonth,
          };
        }

        return {};
      },
    }),
    [findColumn, restrictions, companyId, goalGroups, t],
  );

  const columnDefs = useMemo(
    () =>
      prepareColumns(
        columnsData.items,
        cellEditorParamsMap,
        cellHandlers,
        isReadOnly,
        goalGroups,
      ),
    [columnsData, cellEditorParamsMap, cellHandlers, isReadOnly, goalGroups],
  );

  const { data: totalsRow } = useTotalsRowApi({
    enabled: chosenLevel === MP_LEVELS.MEDIA_PLAN,
  });

  const autoGroupColumnDef = useMemo<AutoGroupColumnDef<MediaPlan.MediaData>>(
    () =>
      colDefWithErrorHandling({
        headerName: 'Media',
        colId: 'name',
        field: 'name',
        minWidth: 350,
        flex: 2,
        pinned: 'left',
        valueGetter: (params) => {
          // this value getter helps grid to understand when need to pass fresh params.data to cellRenderer
          return JSON.stringify({
            name: params.data?.name,
            hasChildren: params.data?.hasChildren,
            mediaRowProps: params.data?.mediaRowProps,
          });
        },
        cellRendererParams: {
          innerRenderer: NameColumnInnerRenderer,
          innerRendererParams: {
            channels: channelsMap,
          },
        },
        cellEditor: TextEditor,
        cellEditorParams: (
          params: CustomCellEditorProps<MediaPlan.MediaData>,
        ) => ({
          ...params,
          value: params.data.name,
        }),
        onCellValueChanged: cellHandlers.name,
        editable: (params) => {
          return (
            !isArchivedVersion &&
            !isReadOnly &&
            (params.data?.levelType === 'media_row' ||
              params.data?.levelType === 'activity')
          );
        },
        lockPosition: 'left',
      }),
    [cellHandlers, channelsMap, isReadOnly],
  );

  const defaultColDef = useMemo<ColDef<MediaPlan.MediaData>>(
    () => ({
      sortable: false,
      minWidth: 200,
      flex: 1,
      headerComponentParams: {
        innerHeaderComponent: TruncatedColumnHeader,
      },
      cellClassRules: {
        'bg-ui-secondary font-semibold': (params) => !!params.node.rowPinned,
      },
    }),
    [],
  );

  const isServerSideGroup = useCallback(
    (mediaData: MediaPlan.MediaData) => mediaData.hasChildren,
    [],
  );

  const getServerSideGroupKey = useCallback(
    (mediaData: MediaPlan.MediaData) => mediaData.id,
    [],
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<MediaPlan.MediaData>) => params.data.id,
    [],
  );

  const setMediaDataToDelete = useSetMediaDataToDelete();

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<MediaPlan.MediaData>) => {
      if (
        !params.node?.data ||
        params.node.data.id === TOTALS_ROW_ID ||
        isArchivedVersion
      ) {
        return [];
      }

      const mediaData = params.node.data;

      return [
        mediaData.levelType !== MediaPlan.LevelTypeEnum.enum.media_row
          ? {
              name: t('Add row'),
              menuItem: AddRowMenuItem,
              suppressCloseOnSelect: true,
              disabled: isReadOnly,
              menuItemParams: {
                mediaData: params.node.data,
              },
            }
          : null,
        mediaData.levelType === MediaPlan.LevelTypeEnum.enum.media_row
          ? {
              name: t('Duplicate'),
              menuItem: MenuItemBase,
              disabled: isReadOnly,
              menuItemParams: {
                startAdornment: <Duplicate />,
                endAdornment: <ChevronRight />,
              },
              subMenu: [
                {
                  name: 'Duplicate media row',
                  menuItem: DuplicateMediaRow,
                  menuItemParams: { mediaData },
                },
              ],
            }
          : null,
        {
          name: t('Delete row'),
          menuItem: MenuItemBase,
          action: () => setMediaDataToDelete(mediaData),
          disabled: isReadOnly || !params.node?.data?.canBeDeleted,
          tooltip: params.node?.data?.canBeDeleted
            ? ''
            : t('There are media rows in this item. Delete media rows first'),
          menuItemParams: {
            startAdornment: <Delete className="fill-current" />,
            className: 'text-text-error',
          },
        },
        {
          name: t('Clear value'),
          menuItem: MenuItemBase,
          action: cellHandlers.clear,
          disabled: isReadOnly || getIsClearValueDisabled(params),
          menuItemParams: {
            startAdornment: <Erase />,
          },
        },
      ].filter((item) => item !== null);
    },
    [
      t,
      cellHandlers.clear,
      setMediaDataToDelete,
      isReadOnly,
      isArchivedVersion,
    ],
  );

  const serverSideDatasource = useMemo(
    () => ({
      getRows: (params: IServerSideGetRowsParams<MediaPlan.MediaData>) => {
        const { groupKeys, startRow, endRow } = params.request;

        const lastExpandedRowId = groupKeys.at(-1);

        MediaPlan.getMediaData({
          urlParams: {
            mediaDataId: lastExpandedRowId ?? chosenId,
          },
          params: {
            offset: startRow ?? 0,
            limit: endRow ?? CACHE_BLOCK_SIZE,
            getChildren:
              chosenLevel === MP_LEVELS.MEDIA_PLAN || groupKeys.length > 0,
            flightVersionId,
          },
        })
          .then(({ data }) => {
            params.success({
              rowData: data?.items ?? [],
            });
          })
          .catch(() => {
            params.fail();
          });
      },
    }),
    [chosenId, chosenLevel, flightVersionId],
  );

  const sideBar = useMemo<
    SideBarDef | string | string[] | boolean | null
  >(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: t('Columns'),
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
            suppressColumnSelectAll: true,
          },
        },
      ],
    };
  }, [t]);

  const handleColumnsChange = useCallback(
    (
      params:
        | ColumnVisibleEvent<MediaPlan.MediaData>
        | ColumnMovedEvent<MediaPlan.MediaData>,
    ) => {
      if (params.type === 'columnMoved' && !params.finished) {
        return;
      }

      if (!isUserColumnChange(params.source)) {
        return;
      }

      const visibleColumns = params.api.getAllDisplayedColumns();

      const filteredColumns = visibleColumns.filter(
        (c) =>
          c.getColDef().field !== 'name' &&
          !c.getColDef().suppressColumnsToolPanel,
      );

      const newColumns = filteredColumns.map((c) => c.getColId());

      updateColumns(newColumns);
    },
    [updateColumns],
  );

  const components = useMemo(
    () => ({
      agTooltipComponent: InfoTooltip,
    }),
    [],
  );

  const pinnedTopRowData = useMemo(() => {
    if (chosenLevel === MP_LEVELS.MEDIA_PLAN) {
      if (totalsRow && (totalsRow as MediaPlan.MediaData).id) {
        return [{ ...totalsRow, id: TOTALS_ROW_ID, label: t('Totals') }];
      }
    }

    return [];
  }, [totalsRow, chosenLevel, t]);

  const context = useMemo(() => {
    return createContext({
      rowIdsToExpand: [],
      t,
      isAddRowDisabled: isReadOnly || !!isArchivedVersion,
    });
  }, [t, isReadOnly, isArchivedVersion]);

  const onGridReady = useCallback(
    (event: GridReadyEvent<MediaPlan.MediaData>) => {
      setGridApi(event.api);
    },
    [setGridApi],
  );

  const onGridPreDestroyed = useCallback(() => {
    setGridApi(null);
  }, [setGridApi]);

  return (
    <AddRowProvider>
      <DeleteMediaDataProvider>
        {gridApi && (
          <LeavePageModal
            isUpdatePending={isUpdateColumnPending}
            gridApi={gridApi}
          />
        )}
        <AgGridReact
          components={components}
          onGridReady={onGridReady}
          rowModelType="serverSide"
          cacheBlockSize={CACHE_BLOCK_SIZE}
          enableCellEditingOnBackspace
          pinnedTopRowData={pinnedTopRowData}
          treeData
          getRowId={getRowId}
          isServerSideGroup={isServerSideGroup}
          getServerSideGroupKey={getServerSideGroupKey}
          serverSideDatasource={serverSideDatasource}
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getContextMenuItems={getContextMenuItems}
          stopEditingWhenCellsLoseFocus
          sideBar={sideBar}
          onColumnVisible={handleColumnsChange}
          onColumnMoved={handleColumnsChange}
          suppressServerSideFullWidthLoadingRow
          loading={false}
          tooltipShowDelay={TOOLTIP_SHOW_DELAY}
          context={context}
          onGridPreDestroyed={onGridPreDestroyed}
        />
      </DeleteMediaDataProvider>
    </AddRowProvider>
  );
};

export default TableGrid;

function getPrimaryEntitiesUrnMap(
  mediaData: MediaPlan.MediaData,
  primaryFields: string[],
) {
  return primaryFields.reduce((acc, fieldId) => {
    const value = mediaData.attributes[fieldId]?.value;

    if (Array.isArray(value)) {
      return { ...acc, [fieldId]: value };
    }

    if (typeof value === 'string') {
      return { ...acc, [fieldId]: [value] };
    }

    return acc;
  }, {});
}

function deriveDefaultMonth(
  isStartDateCol?: boolean,
  restrictions?: MediaPlan.RestrictionsResponse,
) {
  if (!restrictions) {
    return;
  }

  const minDate = parseISO(restrictions.minDate);
  const maxDate = parseISO(restrictions.maxDate);

  return isStartDateCol ? minDate : maxDate;
}
